import { useState, useEffect } from 'react';
import { BookOpen, Users, CheckCircle, Clock, AlertCircle, Loader2, ChevronRight } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';

interface Course {
  course_instance_id: string;
  course_name: string;
  exam_title: string;
  sensor_role: string;
  assigned_at: string;
  start_date: string | null;
  end_date: string | null;
  pending_count: number;
  graded_count: number;
  total_sessions: number;
}

export default function SensorDashboard() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/sensor/courses');
      const data = await response.json();

      if (data.success) {
        setCourses(data.courses);
      } else {
        setError(data.message || 'Failed to fetch courses');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-kf-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
        <AlertCircle className="h-5 w-5" />
        <span>{error}</span>
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-8 text-center">
        <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Ingen kurs tildelt
        </h3>
        <p className="text-gray-600">
          Du har ikke blitt tildelt noen kurs ennå. Vennligst kontakt en administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Sensor Dashboard</h2>
        <p className="text-sm text-gray-600 mt-1">
          Dine tildelte kurs og sensureringsfremgang
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-kf-blue-100 rounded-lg">
              <BookOpen className="h-5 w-5 text-kf-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Totalt antall kurs</p>
              <p className="text-2xl font-bold text-gray-900">{courses.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Venter på sensurering</p>
              <p className="text-2xl font-bold text-gray-900">
                {courses.reduce((sum, c) => sum + parseInt(c.pending_count.toString()), 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Sensurert</p>
              <p className="text-2xl font-bold text-gray-900">
                {courses.reduce((sum, c) => sum + parseInt(c.graded_count.toString()), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Course list */}
      <div className="space-y-4">
        {courses.map((course) => {
          const pendingCount = parseInt(course.pending_count.toString());
          const gradedCount = parseInt(course.graded_count.toString());
          const totalCount = parseInt(course.total_sessions.toString());
          const progressPercent = totalCount > 0 ? (gradedCount / totalCount) * 100 : 0;

          return (
            <div
              key={course.course_instance_id}
              className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/sensor/course/${course.course_instance_id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <BookOpen className="h-5 w-5 text-kf-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {course.course_name}
                    </h3>
                    {pendingCount > 0 && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                        {pendingCount} venter
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 mb-4">{course.exam_title}</p>

                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">
                        {totalCount} {totalCount === 1 ? 'kandidat' : 'kandidater'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">
                        {pendingCount} venter
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">
                        {gradedCount} sensurert
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  {totalCount > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Sensureringsfremgang</span>
                        <span>{Math.round(progressPercent)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <ChevronRight className="h-5 w-5 text-gray-400 ml-4" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
